import { type CSSProperties, type FormEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import BrandLogo from '../../components/BrandLogo'
import { FileDropZone } from '../../components/FileDropZone'
import { publicCustomerLinkApi, type PublicSubject } from '../../api/customerLinks'
import { ApiError } from '../../api/client'
import { Button, Callout, Card, CardContent, Checkbox, KeyValueList, Select, TextField } from '../../components/ui'

/**
 * C-09 — pagina pubblica `/c/:token`, fuori dal login e fuori dalla shell. Il token si legge dall'URL, si tiene solo in
 * sessionStorage (scheda corrente) e si toglie subito dalla barra degli indirizzi; alle API va in un header, mai
 * nell'URL. Passi: codice OTP via email → conferma dei dati → documenti e bolletta → IBAN → invio (dopo, il link non
 * vale piu').
 */
const PAGE_STYLE: CSSProperties = {
  minHeight: '100svh',
  paddingTop: 'var(--md-safe-top)',
  paddingBottom: 'var(--md-safe-bottom)',
}
const STORAGE_KEY = 'ecrm_customer_link'

type Step = 'loading' | 'invalid' | 'otp' | 'form' | 'done'

function readToken(fromUrl: string | undefined): string {
  if (fromUrl) {
    try { sessionStorage.setItem(STORAGE_KEY, fromUrl) } catch { /* ignore */ }
    window.history.replaceState(null, '', window.location.pathname.replace(/\/c\/.*$/, '/c'))
    return fromUrl
  }
  try { return sessionStorage.getItem(STORAGE_KEY) ?? '' } catch { return '' }
}

function messageOf(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

function isTerminal(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.status === 410 || e.status === 423)
}

export function CustomerLinkPage() {
  const params = useParams<{ token?: string }>()
  const [token] = useState(() => readToken(params.token))
  const [step, setStep] = useState<Step>('loading')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [emailHint, setEmailHint] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [session, setSession] = useState<string | null>(null)
  const [subject, setSubject] = useState<PublicSubject | null>(null)
  const [confirmed, setConfirmed] = useState(true)
  const [corrections, setCorrections] = useState('')
  const [kind, setKind] = useState('documento')
  const [uploaded, setUploaded] = useState(0)
  const [iban, setIban] = useState('')
  const [busy, setBusy] = useState(false)

  function fail(e: unknown, fallback: string) {
    if (isTerminal(e)) {
      setMessage(messageOf(e, 'Link non valido.'))
      setStep('invalid')
      try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    } else if (e instanceof ApiError && e.status === 401) {
      setSession(null); setStep('otp'); setError(messageOf(e, fallback))
    } else {
      setError(messageOf(e, fallback))
    }
  }

  useEffect(() => {
    if (!token) { setMessage('Link non valido.'); setStep('invalid'); return }
    publicCustomerLinkApi.open(token)
      .then(r => { setEmailHint(r.emailHint); setStep('otp') })
      .catch(e => fail(e, 'Link non valido.'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  async function sendCode() {
    setBusy(true); setError(''); setInfo('')
    try {
      await publicCustomerLinkApi.sendOtp(token)
      setInfo(emailHint ? `Ti abbiamo inviato un codice a ${emailHint}.` : 'Ti abbiamo inviato un codice via email.')
    } catch (e) { fail(e, 'Invio del codice non riuscito.') } finally { setBusy(false) }
  }

  async function verify(ev: FormEvent) {
    ev.preventDefault()
    setBusy(true); setError('')
    try {
      const r = await publicCustomerLinkApi.verifyOtp(token, code)
      const data = await publicCustomerLinkApi.data(token, r.session)
      setSession(r.session); setSubject(data); setUploaded(data.uploadedCount)
      if (data.kinds[0]) setKind(data.kinds[0].value)
      setCode(''); setInfo(''); setStep('form')
    } catch (e) { fail(e, 'Codice non corretto.') } finally { setBusy(false) }
  }

  async function upload(files: File[]) {
    if (!session) return
    setBusy(true); setError(''); setInfo('')
    try {
      for (const f of files) {
        const r = await publicCustomerLinkApi.upload(token, session, f, kind)
        setUploaded(r.uploadedCount)
      }
      setInfo(files.length === 1 ? 'File caricato.' : 'File caricati.')
    } catch (e) { fail(e, 'Caricamento non riuscito.') } finally { setBusy(false) }
  }

  async function submit(ev: FormEvent) {
    ev.preventDefault()
    if (!session) return
    setBusy(true); setError('')
    try {
      await publicCustomerLinkApi.submit(token, session, confirmed, corrections.trim(), iban.trim())
      try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
      setSession(null); setSubject(null); setStep('done')
    } catch (e) { fail(e, 'Invio non riuscito.') } finally { setBusy(false) }
  }

  const name = subject ? [subject.firstName, subject.lastName].filter(Boolean).join(' ') || subject.companyName : null
  const address = subject ? [subject.address, subject.postalCode, subject.city, subject.province].filter(Boolean).join(', ') : ''

  return (
    <div style={PAGE_STYLE} className="bg-body d-flex justify-content-center">
      <div className="w-100 p-3" style={{ maxWidth: 640 }}>
        <Card variant="outlined" padding="md">
          <CardContent>
            <div className="text-center mb-4"><BrandLogo height={56} /></div>

            {step === 'loading' && <p className="text-muted">Verifica del link…</p>}

            {step === 'invalid' && <Callout tone="error">{message}</Callout>}

            {step === 'done' && (
              <Callout tone="success" title="Grazie, abbiamo ricevuto i tuoi dati">
                Il tuo consulente li controllerà a breve. Questo link ora non è più attivo.
              </Callout>
            )}

            {error && step !== 'invalid' && <Callout tone="error" className="mb-3">{error}</Callout>}
            {info && <Callout tone="info" className="mb-3">{info}</Callout>}

            {step === 'otp' && (
              <form onSubmit={e => void verify(e)}>
                <h1 className="md-typescale-title-large mb-2">Accedi alla tua pratica</h1>
                <p className="md-typescale-body-medium">
                  Per sicurezza ti mandiamo un codice di 6 cifre {emailHint ? <>all'indirizzo <strong>{emailHint}</strong></> : 'via email'}.
                </p>
                <Button variant="tonal" icon="mail" loading={busy} onClick={() => void sendCode()}>Invia il codice</Button>
                <div className="mt-3">
                  <TextField
                    label="Codice"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={e => setCode(e.currentTarget.value.replace(/\D/g, ''))}
                  />
                </div>
                <Button variant="filled" type="submit" className="mt-3" loading={busy} disabled={code.length !== 6}>Continua</Button>
              </form>
            )}

            {step === 'form' && subject && (
              <form onSubmit={e => void submit(e)}>
                <h1 className="md-typescale-title-large mb-1">I tuoi dati</h1>
                {subject.practice && <p className="text-muted md-typescale-body-small">Pratica {subject.practice}</p>}
                <KeyValueList
                  items={[
                    { label: 'Nome', value: name || '—' },
                    { label: 'Codice fiscale', value: subject.fiscalCode || '—' },
                    ...(subject.vatNumber ? [{ label: 'Partita IVA', value: subject.vatNumber }] : []),
                    { label: 'Email', value: subject.email || '—' },
                    { label: 'Telefono', value: subject.phone || '—' },
                    { label: 'Indirizzo', value: address || '—' },
                  ]}
                />
                <div className="mt-3">
                  <Checkbox label="I dati sono corretti" checked={confirmed} onChange={e => setConfirmed(e.currentTarget.checked)} />
                </div>
                {!confirmed && (
                  <div className="mt-2">
                    <TextField
                      label="Cosa va corretto?"
                      multiline
                      rows={3}
                      maxLength={2000}
                      value={corrections}
                      onChange={e => setCorrections(e.currentTarget.value)}
                    />
                  </div>
                )}

                <h2 className="md-typescale-title-medium mt-4 mb-2">Documenti e bolletta</h2>
                <p className="md-typescale-body-small text-muted">PDF o foto (JPG, PNG, WebP), massimo 10 MB per file. Caricati: {uploaded}.</p>
                <Select label="Tipo di documento" value={kind} onChange={e => setKind(e.currentTarget.value)}>
                  {subject.kinds.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                </Select>
                <div className="mt-2">
                  <FileDropZone busy={busy} onFiles={files => upload(files)} label="Trascina qui il file oppure tocca per sceglierlo" />
                </div>

                <h2 className="md-typescale-title-medium mt-4 mb-2">IBAN (facoltativo)</h2>
                <TextField
                  label="IBAN"
                  uppercase
                  autoComplete="off"
                  maxLength={42}
                  value={iban}
                  onChange={e => setIban(e.currentTarget.value)}
                  supportingText="Il conto su cui addebitare o accreditare, se richiesto dalla tua pratica."
                />

                <Button
                  variant="filled"
                  type="submit"
                  className="mt-4"
                  loading={busy}
                  disabled={!confirmed && corrections.trim() === ''}
                >
                  Invia
                </Button>
                <p className="md-typescale-body-small text-muted mt-2">Dopo l'invio questo link non sarà più utilizzabile.</p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
